import styles from "./ViewStatistics.module.scss";
import { useViewStatisticsPage } from "./hooks/useViewStatisticsPage";
import { LineChart } from "../../common/components/charts/LineChart";
import { DefaultSection } from "../../common/components/common/DefaultSection";
import { BarChart } from "../../common/components/charts/BarChart";
import {
  IfFalse,
  IfTrue,
  RenderWhen,
} from "../../common/infrastructure/components/RenderWhen";
import { ChartType, StatisticDetailedItemType } from "../../common/enums";
import { ViewStatisticsItem } from "./ViewStatisticsItem";
import { useMemo, useState } from "react";
import { AppDialog } from "../../common/components/AppDialog/AppDialog";
import { ViewStatisticsDetailedItem } from "./components/ViewStatisticsDetailedItem";
import { IconButton } from "../../common/components/button/iconButton/IconButton";
import { IconType } from "../../common/icons/Icon";
import { Tag } from "../../common/components/common/Tag/Tag";

export const ViewStatistics = () => {
  const {
    subtitle,
    result,
    isLoading,
    handleExportExcel,
    selectedHallNames,
  } = useViewStatisticsPage();

  const [selectedDetailItemsKey, setSelectedDetailItemsKey] =
    useState<string>();

  const selectedStatisticItem = useMemo(() => {
    return result?.mainInformation.find(
      (x) => x.key === selectedDetailItemsKey
    );
  }, [selectedDetailItemsKey]);

  const selectedDetailMetadata = useMemo(() => {
    return selectedStatisticItem?.detailedItemsMetadata;
  }, [selectedDetailItemsKey]);

  const detailItemsToShow = useMemo(() => {
    return result?.detailedItems
      .filter((x) => {
        return selectedDetailMetadata?.some((y) => {
          const isInstructorVisits =
            y.type === StatisticDetailedItemType.InstructorVisits;
          const isInstructorPlans =
            y.type === StatisticDetailedItemType.InstructorPlans;
          const isMatchingId = y.id === x.id;
          const isMatchingUserId = y.id === x.userId;
          const isMatchingType = y.type === x.type;

          if (isInstructorVisits) {
            return (
              isMatchingUserId &&
              x.type === StatisticDetailedItemType.Reservation
            );
          }

          if (isInstructorPlans) {
            return (
              isMatchingId &&
              (x.type === StatisticDetailedItemType.FamilyPlan ||
                x.type === StatisticDetailedItemType.UserPlan)
            );
          }

          return isMatchingId && isMatchingType;
        });
      })
      .sort(
        (a, b) =>
          new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()
      );
  }, [selectedDetailItemsKey]);

  if (isLoading) {
    return;
  }

  return (
    <div className={styles.root}>
      <DefaultSection className={styles.root_info_section}>
        <div className={styles.root_header_title}>
          <span>Репорти</span>
          {result && (
            <div data-onboarding="stats-export-btn">
              <IconButton
                iconType={IconType.Download}
                iconHeight="18px"
                iconWidth="18px"
                tooltip="Експорт в Excel"
                onClick={handleExportExcel}
              />
            </div>
          )}
        </div>
        <div className={styles.root_header_subtitle}>({subtitle})</div>
        <RenderWhen condition={selectedHallNames.length > 0}>
          <div className={styles.root_header_halls}>
            <span className={styles.root_header_halls_label}>Зали:</span>
            {selectedHallNames.map((name, idx) => (
              <Tag
                key={`${name}_${idx}`}
                label={name}
                backgroundColor={"#c4b5fd"}
              />
            ))}
          </div>
        </RenderWhen>
        {result?.mainInformation.map((x, i) => (
          <div className={styles.root_info_section_pair} key={`${x.name}_${i}`}>
            <RenderWhen condition={!!x.name}>
              <IfTrue>
                <ViewStatisticsItem
                  item={x}
                  onClick={setSelectedDetailItemsKey}
                />
              </IfTrue>
              <IfFalse>
                <hr />
              </IfFalse>
            </RenderWhen>
          </div>
        ))}
      </DefaultSection>
      {result?.data?.map((x) => (
        <div key={x.chartType}>
          <RenderWhen condition={x.chartType === ChartType.Bar}>
            <BarChart className={styles.root_graph} data={x} />
          </RenderWhen>

          <RenderWhen condition={x.chartType === ChartType.Line}>
            <LineChart className={styles.root_graph} data={x} />
          </RenderWhen>
        </div>
      ))}

      <AppDialog
        header={"Обзор на детайлите"}
        visible={!!selectedDetailItemsKey}
        onHide={() => setSelectedDetailItemsKey(undefined)}
        className={styles.dialog}
        contentClassName={styles.dialog_content}
      >
        <ViewStatisticsDetailedItem
          items={detailItemsToShow}
          selectedDetailItemsKey={selectedDetailItemsKey}
        />
      </AppDialog>
    </div>
  );
};
